import React from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { CloudOff as CloudOffIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/auth/authContext';
import {
  OFFLINE_AUTH_TTL_DAYS,
  OFFLINE_AUTH_WARN_AT_DAYS,
  OFFLINE_AUTH_HARD_WARN_AT_DAYS,
} from '../../contexts/auth/offlineAuthSnapshot';

/**
 * Persistent thin banner shown above offline-safe pages so the handler
 * always knows they are operating in offline mode and how much offline
 * eligibility they have left. Color escalates as TTL approaches.
 */
export const OfflineBanner: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const { authMode, offlineDaysRemaining, offlineEligibleUntil } = useAuth();
  if (authMode !== 'offline') return null;

  const days = typeof offlineDaysRemaining === 'number' ? offlineDaysRemaining : 0;
  const elapsed = OFFLINE_AUTH_TTL_DAYS - days;
  let severity: 'info' | 'warning' | 'error' = 'info';
  if (elapsed >= OFFLINE_AUTH_HARD_WARN_AT_DAYS) severity = 'error';
  else if (elapsed >= OFFLINE_AUTH_WARN_AT_DAYS) severity = 'warning';

  const expiryLabel = (() => {
    if (!offlineEligibleUntil) return 'until you reconnect';
    try {
      const d = new Date(offlineEligibleUntil);
      const formatted = d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      return `until ${formatted}`;
    } catch {
      return 'until you reconnect';
    }
  })();

  const remainingLabel = (() => {
    if (days >= 1) {
      const rounded = Math.floor(days);
      return `${rounded} day${rounded === 1 ? '' : 's'} remaining`;
    }
    const hours = Math.max(0, Math.floor(days * 24));
    return `${hours} hour${hours === 1 ? '' : 's'} remaining`;
  })();

  return (
    <Alert
      severity={severity}
      icon={<CloudOffIcon fontSize="inherit" />}
      role="status"
      aria-live="polite"
      sx={{
        borderRadius: 0,
        py: compact ? 0.25 : 0.5,
        px: compact ? 1 : 2,
        '& .MuiAlert-message': { width: '100%' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          Offline mode — synced changes will be sent when you reconnect.
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
          Offline ready {expiryLabel} ({remainingLabel})
        </Typography>
      </Box>
    </Alert>
  );
};

export default OfflineBanner;
